import React from 'react';
import {Modal, Button, Table} from 'react-bootstrap';
import {dateToString, numberWithCommas} from '../../tools';
import {RRFuelInvoiceType} from '../../types/RRFuelInvoiceType';

type PriceCalcModalProps = {
    show: boolean;
    onHide: () => void;
    price: number | null;
    truckLabel?: string | null;
    invoicesSlice?: RRFuelInvoiceType[] | null;
    note?: string;
};

const PriceCalcModal: React.FC<PriceCalcModalProps> = ({show, onHide, price, truckLabel, invoicesSlice, note}) => {
    // compute weighted average from invoicesSlice for display (same logic as report)
    let sliceAvg = 0;
    let sliceTotalFuel = 0;
    let sliceTotalKm = 0;
    // arithmetic average of invoice prices (for the 'Цена (€)' column in totals)
    let sliceAvgPrice = 0;
    // sum of per-invoice price/km (to show in final 'Всичко разходи' row)
    let sliceSumPricePerKm = 0;
    if (invoicesSlice && invoicesSlice.length > 0) {
        let weightedSum = 0;
        let weightSum = 0;
        let sumPrice = 0;
        invoicesSlice.forEach(inv => {
            const invPrice = Number(inv.price || 0);
            const invFuel = Number(inv.fuelAmount || 0);
            const invKm = Number(inv.totalKm || 0);
            sumPrice += invPrice;
            const avg = (inv.avgPriceForKm !== undefined && inv.avgPriceForKm !== null) ? Number(inv.avgPriceForKm) : ((invKm && invKm > 0) ? ((invFuel * invPrice) / invKm) : (invFuel * invPrice || 0));
            const w = (invKm && invKm > 0) ? invKm : (invFuel && invFuel > 0 ? invFuel : 1);
            if (!isNaN(avg) && isFinite(avg)) {
                weightedSum += avg * w;
                weightSum += w;
            }
            // accumulate totals
            sliceTotalFuel += invFuel;
            sliceTotalKm += invKm;
            // compute per-invoice price/km used in the table and accumulate
            const invPricePerKm = (inv.avgPriceForKm !== undefined && inv.avgPriceForKm !== null) ? Number(inv.avgPriceForKm) : ((invKm && invKm > 0) ? ((invFuel * invPrice) / invKm) : 0);
            if (!isNaN(invPricePerKm) && isFinite(invPricePerKm)) sliceSumPricePerKm += invPricePerKm;
        });
        sliceAvg = weightSum > 0 ? (weightedSum / weightSum) : 0;
        sliceAvgPrice = invoicesSlice.length > 0 ? (sumPrice / invoicesSlice.length) : 0;
    }
    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Детайли за начина на изчисление на Цена на км</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-3">
                    <strong>Резултат:</strong>&nbsp;{price !== null ? `${numberWithCommas(Number(price.toFixed(2)))} €/км` : '-'}
                </div>

                {/* show truck and chosen invoice info (if provided) */}
                {(truckLabel) && (
                    <div className="mb-2 text-muted small">
                        {truckLabel && <span>Камион: {truckLabel}</span>}
                    </div>
                )}

                {note && <div className={'mb-3 text-muted'}>{note}</div>}

                {invoicesSlice && invoicesSlice.length > 0 && (
                    <>
                        <h5>Фактури използвани за средна цена ({invoicesSlice.length})</h5>
                        <Table bordered size={'sm'}>
                            <thead>
                                <tr>
                                    <th>Номер</th>
                                    <th>Дата</th>
                                    <th className="text-end">Цена (€)</th>
                                    <th className="text-end">Колич. гориво</th>
                                    <th className="text-end">Общо км</th>
                                    <th className="text-end">Цена/км</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoicesSlice.map(inv => (
                                    <tr key={inv.id || (inv.invNumber || Math.random())}>
                                        <td>{inv.invNumber}</td>
                                        <td>{dateToString(inv.invDate)}</td>
                                        <td className="text-end">{numberWithCommas(Number(inv.price || 0))}</td>
                                        <td className="text-end">{numberWithCommas(Number(inv.fuelAmount || 0))}</td>
                                        <td className="text-end">{numberWithCommas(Number(inv.totalKm || 0))}</td>
                                        <td className="text-end">{numberWithCommas(Number(((inv.avgPriceForKm !== undefined && inv.avgPriceForKm !== null) ? inv.avgPriceForKm : ((inv.totalKm && inv.totalKm > 0) ? ((Number(inv.fuelAmount || 0) * Number(inv.price || 0)) / Number(inv.totalKm || 1)) : 0)).toFixed(4)))}</td>
                                    </tr>
                                ))}
                                {/* totals row */}
                                <tr className="table-secondary fw-bold">
                                    <td><strong>Общо/средно</strong></td>
                                    <td></td>
                                    <td className="text-end">{numberWithCommas(Number(sliceAvgPrice.toFixed(2)))}</td>
                                    <td className="text-end">{numberWithCommas(Number(sliceTotalFuel.toFixed(2)))}</td>
                                    <td className="text-end">{numberWithCommas(Number(sliceTotalKm || 0))}</td>
                                    <td className="text-end">{numberWithCommas(Number(sliceAvg.toFixed(4)))}</td>
                                </tr>
                             </tbody>
                         </Table>
                         <div className="mt-2"><strong>Средна цена (от избраните фактури):</strong>&nbsp;{numberWithCommas(Number(sliceAvg.toFixed(4)))} €/км</div>
                     </>
                 )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Затвори</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default PriceCalcModal;
